import { CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpParams } from '@angular/common/http';
import { Component, computed, effect, inject, signal, viewChildren } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { EEventType } from '@enums/event-tracking.enum';
import { IAnalyticsNode, ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CampaignsService } from '@pages/campaigns/campaigns.service';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { CampaignDetailsPlatformFeaturesService } from '@shared/components/profile-platform-feature-filter/profile-platform-feature-filter.service';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { filter, Observable } from 'rxjs';
import { EDetailLevel, ICampaignExportApi, ICampaignExportDialogData } from './campaign-export-dialog.interface';
import { CampaignExportDialogStore } from './campaign-export-dialog.store';
import { CampaignExportPanelComponent } from './campaign-export-panel/campaign-export-panel.component';

@Component({
  imports: [
    SideDialogComponent,
    TranslateModule,
    MatButtonModule,
    IconComponent,
    DotsSpinnerComponent,
    NgxSkeletonLoaderModule,
    CampaignExportPanelComponent,
    CdkDropList,
    CdkDrag,
    CdkDragPlaceholder,
  ],
  providers: [CampaignExportDialogStore],
  selector: 'app-campaign-export-dialog',
  templateUrl: './campaign-export-dialog.component.html',
})
export class CampaignExportDialogComponent {
  public readonly campaignAnalytics = signal<ICampaignAnalytics | null>(null);
  public readonly analyticsNodes = signal<IAnalyticsNode[] | null>(null);

  private readonly manualExpandedId = signal<string | null | undefined>(undefined);
  public readonly expandedDirty = signal<boolean>(false);

  private latchedInitialExpandedId: string | undefined = undefined;

  public readonly effectiveExpandedId = computed(() => {
    const manual = this.manualExpandedId();

    if (manual !== undefined) {
      return manual;
    }

    if (this.latchedInitialExpandedId) {
      return this.latchedInitialExpandedId;
    }

    const firstId = this.store.campaignExports$()[0]?.id ?? null;

    if (firstId) {
      this.latchedInitialExpandedId = firstId;
    }

    return firstId;
  });

  public readonly data = inject<ICampaignExportDialogData>(MAT_DIALOG_DATA);
  public readonly store = inject(CampaignExportDialogStore);

  private readonly dialogRef = inject(MatDialogRef<CampaignExportDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly campaignsService = inject(CampaignsService);
  private readonly campaignDetailsPlatformFeaturesService = inject(CampaignDetailsPlatformFeaturesService);
  private readonly eventTrackingService = inject(EventTrackingService);

  private readonly panelComponents = viewChildren(CampaignExportPanelComponent);

  private lastHandledCreatedExportId: string | null = null;

  constructor() {
    this.campaignAnalytics.set(this.data.campaignAnalytics);
    this.analyticsNodes.set(this.data.analyticsNodes);

    const campaignId = this.data.campaignAnalytics?.id;

    if (campaignId) {
      this.store.fetchCampaignExports({ campaignId });
    }

    this.expandNewlyCreatedExportEffect();
    this.fetchPlatformFeatureKpisEffect();
    this.showStoreErrorsEffect();
  }

  public onDrop(event: CdkDragDrop<ICampaignExportApi[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const campaignExports = [...this.store.campaignExports$()];
    moveItemInArray(campaignExports, event.previousIndex, event.currentIndex);

    // Stamp the freshly dropped order onto every entry so a later, unrelated state patch can't
    // re-sort the list back using stale order.order values from before the drop.
    const reorderedCampaignExports = campaignExports.map((campaignExport, index) => ({
      ...campaignExport,
      order: campaignExport.order ? { ...campaignExport.order, order: index } : campaignExport.order,
    }));

    const movedCampaignExport = reorderedCampaignExports[event.currentIndex];
    const campaignId = this.campaignAnalytics()?.id;

    if (!movedCampaignExport?.order?.id || !movedCampaignExport.id || !campaignId) {
      // Nothing to persist against — this report has no order record yet, so just reorder locally.
      this.store.setCampaignExports(reorderedCampaignExports);

      return;
    }

    // The store applies the reorder optimistically and rolls it back if the request fails.
    this.store.updateCampaignExportOrder({
      campaignExportId: movedCampaignExport.id,
      campaignExports: reorderedCampaignExports,
      campaignId,
      order: event.currentIndex,
      orderId: movedCampaignExport.order.id,
    });
  }

  public onPanelOpenAttempt(campaignExportId: string, enterNameEditModeAfter = false): void {
    const currentId = this.effectiveExpandedId();

    if (currentId === campaignExportId) {
      if (enterNameEditModeAfter) {
        this.findPanel(campaignExportId)?.enterNameEditMode();
      }

      return;
    }

    if (currentId && this.expandedDirty()) {
      // The panel's own MatExpansionPanel already toggles itself open on header click, ahead of
      // our decision — force it back closed immediately so it doesn't sit open while we wait for
      // the user to respond to the confirm dialog.
      this.findPanel(campaignExportId)?.forceCollapse();
      this.confirmSwitchPanel(campaignExportId, enterNameEditModeAfter);

      return;
    }

    this.expandPanel(campaignExportId, enterNameEditModeAfter);
  }

  public onNameEditRequested(campaignExportId: string): void {
    this.onPanelOpenAttempt(campaignExportId, true);
  }

  public onCloseAttempt(campaignExportId: string): void {
    if (this.effectiveExpandedId() !== campaignExportId) {
      return;
    }

    if (this.expandedDirty()) {
      this.findPanel(campaignExportId)?.reaffirmExpanded();

      this.confirmDiscardChanges()
        .pipe(filter(Boolean))
        .subscribe(() => {
          this.findPanel(campaignExportId)?.discardChanges();
          this.collapsePanel(campaignExportId);
        });

      return;
    }

    this.collapsePanel(campaignExportId);
  }

  public onDirtyChange(campaignExportId: string, dirty: boolean): void {
    if (this.effectiveExpandedId() === campaignExportId) {
      this.expandedDirty.set(dirty);
    }
  }

  public onSaved(campaignExport: ICampaignExportApi): void {
    this.store.updateCampaignExportInList(campaignExport);
    this.expandedDirty.set(false);
  }

  public onDeleteRequested(campaignExportId: string): void {
    this.store.deleteCampaignExport({
      id: campaignExportId,
      // The store removes the row optimistically - only report the event once the delete is
      // actually confirmed, since a failed request reverts the removal but can't retract the event.
      onDeleted: () => {
        this.eventTrackingService.track(EEventType.campaignReportDeleted, {
          campaignId: this.campaignAnalytics()?.id,
          campaignReportId: campaignExportId,
        });
      },
    });

    if (this.effectiveExpandedId() !== campaignExportId) {
      return;
    }

    const remaining = this.store.campaignExports$().filter((campaignExport) => campaignExport.id !== campaignExportId);
    const nextId = remaining[0]?.id;

    if (nextId) {
      this.expandPanel(nextId);

      return;
    }

    this.manualExpandedId.set(null);
    this.expandedDirty.set(false);
  }

  public onAddReportClick(): void {
    if (this.store.busy$()) {
      return;
    }

    const currentId = this.effectiveExpandedId();

    if (currentId && this.expandedDirty()) {
      this.confirmDiscardChanges()
        .pipe(filter(Boolean))
        .subscribe(() => {
          this.findPanel(currentId)?.discardChanges();
          this.createNewReport();
        });

      return;
    }

    this.createNewReport();
  }

  public onDoneClick(): void {
    if (!this.expandedDirty()) {
      this.dialogRef.close();

      return;
    }

    this.confirmDiscardChanges()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.dialogRef.close();
      });
  }

  private confirmSwitchPanel(nextCampaignExportId: string, enterNameEditModeAfter = false): void {
    this.confirmDiscardChanges()
      .pipe(filter(Boolean))
      .subscribe(() => {
        const currentId = this.effectiveExpandedId();
        this.findPanel(currentId)?.discardChanges();

        this.expandPanel(nextCampaignExportId, enterNameEditModeAfter);
      });
  }

  private confirmDiscardChanges(): Observable<boolean> {
    return this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          cancelText: this.translate.instant('pages.campaignDetails.export.confirmDialog.goBack'),
          confirmText: this.translate.instant('pages.campaignDetails.export.confirmDialog.discardChanges'),
          message: this.translate.instant('pages.campaignDetails.export.confirmDialog.message'),
          swapButtons: true,
          title: this.translate.instant('pages.campaignDetails.export.confirmDialog.title'),
        },
        width: '400px',
      })
      .afterClosed();
  }

  private expandPanel(campaignExportId: string, enterNameEditModeAfter = false): void {
    const previousId = this.effectiveExpandedId();

    this.manualExpandedId.set(campaignExportId);
    this.expandedDirty.set(false);

    if (previousId && previousId !== campaignExportId) {
      this.findPanel(previousId)?.forceCollapse();
    }

    this.findPanel(campaignExportId)?.forceExpand();

    if (enterNameEditModeAfter) {
      this.findPanel(campaignExportId)?.enterNameEditMode();
    }
  }

  private collapsePanel(campaignExportId: string): void {
    this.manualExpandedId.set(null);
    this.expandedDirty.set(false);
    this.findPanel(campaignExportId)?.forceCollapse();
  }

  private createNewReport(): void {
    const campaignId = this.campaignAnalytics()?.id;

    if (!campaignId) {
      return;
    }

    this.store.setCreatingExport(true);

    const params = new HttpParams().set('joins', 'campaign_exports,campaign_exports.order');

    this.campaignsService.getCampaign(campaignId, params).subscribe({
      error: () => this.store.setCreatingExport(false),
      next: (campaign) => {
        const existingNames = new Set(
          (campaign?.campaignExports ?? []).map((campaignExport) => campaignExport.name?.toLowerCase()),
        );
        let count = (campaign?.campaignExports ?? []).length + 1;
        let name = this.translate.instant('pages.campaignDetails.export.reportName', { count }) as string;

        while (existingNames.has(name.toLowerCase())) {
          count++;
          name = this.translate.instant('pages.campaignDetails.export.reportName', { count });
        }

        this.store.pendingReportCreationSource = 'create';
        this.store.createCampaignExport(this.buildNewCampaignExportPayload(campaignId, name));
      },
    });
  }

  private findPanel(campaignExportId: string | null): CampaignExportPanelComponent | undefined {
    return this.panelComponents().find((panel) => panel.campaignExport().id === campaignExportId);
  }

  private buildNewCampaignExportPayload(campaignId: string, name: string): ICampaignExportApi {
    return {
      allCreatorsSelected: true,
      allKpisSelected: true,
      allPlatformFeaturesSelected: true,
      allTabsSelected: true,
      campaignId,
      contentMatchingTypes: [],
      contentStatuses: [],
      creators: [],
      deliverableStatuses: [],
      detailLevel: EDetailLevel.generalInfo,
      end: null,
      isActive: false,
      kpis: [],
      kpiStatuses: [],
      name,
      observationStatuses: [],
      platformFeatures: [],
      showBudget: false,
      showContentMatchingType: true,
      showContentStatus: true,
      showDeliverableStatus: true,
      showKpiStatus: true,
      showObservationStatus: true,
      start: null,
      tabs: [],
    };
  }

  private showStoreErrorsEffect(): void {
    effect(() => {
      const error = this.store.error$();

      if (!error) {
        return;
      }

      this.snackbarService.openError(this.translate.instant(error));
      this.store.patchState({ error: null });
    });
  }

  private expandNewlyCreatedExportEffect(): void {
    effect(() => {
      const createdId = this.store.lastCreatedCampaignExportId$();

      if (!createdId || createdId === this.lastHandledCreatedExportId) {
        return;
      }

      this.lastHandledCreatedExportId = createdId;
      this.expandPanel(createdId);

      const source = this.store.pendingReportCreationSource;
      this.store.pendingReportCreationSource = null;

      const campaignId = this.campaignAnalytics()?.id;

      if (source === 'create') {
        this.eventTrackingService.track(EEventType.campaignReportCreated, {
          campaignId,
          campaignReportId: createdId,
        });
      } else if (source === 'clone') {
        this.eventTrackingService.track(EEventType.campaignReportCloned, {
          campaignId,
          campaignReportId: createdId,
        });
      }
    });
  }

  private fetchPlatformFeatureKpisEffect(): void {
    effect(() => {
      const campaignAnalytics = this.campaignAnalytics();

      if (!campaignAnalytics) {
        this.store.patchState({ platformFeaturesKpis: [] });

        return;
      }

      const platformFeatures = Array.from(
        this.campaignDetailsPlatformFeaturesService.getCampaignPlatformFeatures(campaignAnalytics),
      );

      if (platformFeatures.length) {
        this.store.fetchPlatformFeatureKpis(platformFeatures);

        return;
      }

      this.store.patchState({ platformFeaturesKpis: [] });
    });
  }
}
